import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import Service, { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { ChartOptions, TimeScaleOptions } from "chart.js";
import dayjs from "dayjs";
import { task } from "ember-concurrency";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type ConsultationPlannedJournalEvent from "parog-web/models/consultation-planned-journal-event";
import type ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type DrugsAndDevicesRefundedJournalEvent from "parog-web/models/drugs-and-devices-refunded-journal-event";
import JournalEventModel from "parog-web/models/journal-event";
import type MedicalEventJournalEvent from "parog-web/models/medical-event-journal-event";
import type FetcherService from "parog-web/services/fetcher";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import type ConfigurationService from "./configuration";
export type Timeframe = "year" | "trimester" | "month";

const jesToFetch: string[] = [
  "AnxietyFilledJournalEvent",
  "BioExamExpectedDosageChangedJournalEvent",
  "BioExamFrequencyChangedJournalEvent",
  "BiologicalActRefundedJournalEvent",
  "CtcaeFilledJournalEvent",
  "HeightMeasuredJournalEvent",
  "InterTreatmentFilledJournalEvent",
  "MedicalActRefundedJournalEvent",
  "MedicalImagingRefundedJournalEvent",
  "OkChimioSnapshotCapturedJournalEvent",
  "PostSurgerySurveyFilledJournalEvent",
  "PreSurgerySurveyFilledJournalEvent",
  "PreTreatmentFilledJournalEvent",
  "TreatmentHistoryChangedJournalEvent"
];

export default class DashboardService extends Service {
  @service declare router: RouterService;
  @service declare fetcher: FetcherService;
  @service declare configuration: ConfigurationService;

  @tracked
  private _timeframe: Timeframe = "trimester";

  set timeframe(timeframe: Timeframe) {
    this._timeframe = timeframe;
    this.fetchGraphDataTask.perform();
  }

  get timeframe(): Timeframe {
    return this._timeframe;
  }

  @tracked
  private _endDate: Date = this.today;

  set endDate(endDate: Date) {
    this._endDate = endDate;
    this.fetchGraphDataTask.perform();
  }

  get endDate(): Date {
    return this._endDate;
  }

  @action
  addOneMonthToDate() {
    this.endDate = dayjs(this.endDate).add(6, "weeks").toDate();
  }

  @action
  substractOneMonthToDate() {
    this.endDate = dayjs(this.endDate).subtract(6, "weeks").toDate();
  }

  @action
  resetTime() {
    this.endDate = this.today;
  }

  get patientId(): string | undefined {
    return this.router.currentURL?.match(/\/patients\/(\d+)/)?.[1];
  }

  fetchGraphData(
    patientId
  ): Promise<
    [
      JournalEventModel[],
      any,
      ConsultationPlannedJournalEvent[],
      DrugsAndDevicesRefundedJournalEvent[],
      ConsultationReportWrittenJournalEvent[],
      BioExamResultsReceivedJournalEvent[],
      MedicalEventJournalEvent[]
    ]
  > {
    if (!patientId) {
      throw `Missing patient id for url: ${this.router.currentURL}`;
    }

    const patientJesReq = JournalEventModel.patientJournalEventsRequest({
      fetcher: this.fetcher,
      patientId: patientId,
      jeTypesToFetch: [
        ...new Set(
          jesToFetch.concat(
            this.configuration.getEnabledVitalSignsJesForPatient(patientId)
          )
        )
      ],
      jeTypesToIgnore: ["BloodPressureMeasuredJournalEvent"],
      occurredAfter: this.timeframeStartDate,
      occurredBefore: this.endDate
    });
    let bloodPressureJesReq;
    if (
      this.configuration
        .getEnabledVitalSignsJesForPatient(patientId)
        .includes("BloodPressureMeasuredJournalEvent")
    ) {
      bloodPressureJesReq = this.fetcher.loadRecords(
        "blood-pressure-session-started-journal-event",
        {
          filter: {
            patient_id: patientId,
            occurred_at_gte: formatToISO8601Date(this.timeframeStartDate)
          },
          include: "blood-pressure-measured-journal-events"
        }
      );
    }

    const consultationPlannedJesReq = this.fetcher.loadRecords(
      "consultation-planned-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate),
          occurred_at_lte: formatToISO8601Date(this.endDate)
        },
        include: "creator"
      }
    );

    const drugsAndDevicesRefundedJesReq = this.fetcher.loadRecords(
      "drugs-and-devices-refunded-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate)
        },
        include:
          "medication-dispenses.medication-presentation.medication-specialty.medication-molecule"
      }
    );

    const consultationReportWrittenJesReq = this.fetcher.loadRecords(
      "consultation-report-written-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate),
          occurred_at_lte: formatToISO8601Date(this.endDate)
        },
        include: "creator,file-attachments"
      }
    );

    const bioExamResultsReceivedJesReq = this.fetcher.loadRecords(
      "bio-exam-results-received-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate)
        },
        include: "creator,file-attachments"
      }
    );

    const medicalEventJesReq = this.fetcher.loadRecords(
      "medical-event-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate),
          occurred_at_lte: formatToISO8601Date(this.endDate)
        },
        include: "creator,file-attachments"
      }
    );

    return Promise.all([
      patientJesReq,
      bloodPressureJesReq,
      consultationPlannedJesReq,
      drugsAndDevicesRefundedJesReq,
      consultationReportWrittenJesReq,
      bioExamResultsReceivedJesReq,
      medicalEventJesReq
    ]);
  }

  fetchGraphDataTask = task({ restartable: true }, async () => {
    await this.fetchGraphData(this.patientId);
  });

  get timeframeStartDate(): Date {
    const date = new Date(this.endDate);
    let weeksAgo;

    if (this.timeframe === "month") weeksAgo = 6;
    else if (this.timeframe === "trimester") weeksAgo = 14;
    else if (this.timeframe === "year") weeksAgo = 52; // 1 year == 52 weeks

    // Go back X weeks ago
    date.setDate(date.getDate() - weeksAgo * 7);

    // Go back at the start of the month if we are using the "year" timeframe
    if (this.timeframe === "year") date.setDate(1);
    return date;
  }

  // Return the end date, at the end of the day
  get timeframeEndDate(): Date {
    return dayjs(this.endDate).endOf("day")?.toDate();
  }

  get scaleXTimeUnit(): TimeScaleOptions["time"]["unit"] {
    switch (this.timeframe) {
      case "month":
        return "day";
      case "trimester":
        return "week";
      case "year":
      default:
        return "month";
    }
  }

  get today(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  get chartOptions(): ChartOptions<"line"> {
    return {
      clip: false, // See https://github.com/chartjs/Chart.js/issues/9372
      scales: {
        x: {
          type: "time",
          grid: { display: false },
          time: {
            displayFormats: {
              week: "DD MMM",
              month: "MMM YY",
              day: "DD MMM"
            },
            unit: this.scaleXTimeUnit
          },
          min: +this.timeframeStartDate,
          max: +this.timeframeEndDate,
          ticks: { maxTicksLimit: 8 }
        },
        y: { display: false }
      },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      layout: {
        padding: {
          top: 4,
          bottom: 4
        }
      }
    };
  }
}

declare module "@ember/service" {
  interface Registry {
    dashboard: DashboardService;
  }
}
